import React, { ReactElement, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import portraits, { defaultPortrait } from "../assets";
import { playSound } from "../effects/sound";
import "./ExecutionDrama.css";

type ExecutionDramaProps = {
  /** An execution, or the Chancellor that the table elected in the Hitler zone. */
  kind: "execution" | "election";
  /** The player who was executed / elected. */
  target: string;
  /** Their look (icon id). */
  icon?: string;
  /** Who chose them (the President). */
  president: string;
  /** True when the player is Hitler (the game is over then). */
  wasHitler: boolean;
  /** Called when the scene is over; the game goes on from there. */
  onDone: () => void;
};

type Phase = "count" | "beat" | "flip" | "done";

const COUNT_FROM = 3;
const STEP_MS = 1000;
const BEAT_MS = 900; // the silence before the card turns
const FLIP_MS = 1000;
/** Nobody has to press the button: the scene moves on by itself after the reveal, the same for every player. */
const LINGER_MS = 12000;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The drama of an execution (or of a Chancellor elected in the Hitler zone): a countdown with a heartbeat, a moment of silence, then a card turns over to show
 * the one thing the table is allowed to learn: whether the player is Hitler. (Their party stays secret.)
 * Everyone sees exactly the same scene, the player in question too, so nothing private is revealed. With reduced motion the card is simply shown at once.
 */
export default function ExecutionDrama(props: ExecutionDramaProps): ReactElement {
  const reduced = useRef(prefersReducedMotion()).current;
  const [count, setCount] = useState(COUNT_FROM);
  const [phase, setPhase] = useState<Phase>(reduced ? "done" : "count");
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const finished = useRef(false);
  const okay = useRef<HTMLButtonElement>(null);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(setTimeout(fn, ms));
  };

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    props.onDone();
  };

  const reveal = () => {
    playSound(props.wasHitler ? "revealHitler" : "revealSafe");
  };

  useEffect(() => {
    if (reduced) {
      reveal();
      return;
    }
    // 3 … 2 … 1, a heartbeat on each, then silence, then the card.
    for (let i = 0; i < COUNT_FROM; i++) {
      later(() => {
        setCount(COUNT_FROM - i);
        playSound("heartbeat", i);
      }, i * STEP_MS);
    }
    const flipAt = COUNT_FROM * STEP_MS;
    later(() => setPhase("beat"), flipAt);
    later(() => {
      setPhase("flip");
      reveal();
    }, flipAt + BEAT_MS);
    later(() => setPhase("done"), flipAt + BEAT_MS + FLIP_MS);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "done") return;
    okay.current?.focus();
    later(finish, LINGER_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
    },
    []
  );

  const skip = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (phase !== "done") {
      reveal();
      setPhase("done");
    }
  };

  const portrait = (props.icon && props.icon !== defaultPortrait && portraits[props.icon]) || undefined;
  const flipped = phase === "flip" || phase === "done";
  const election = props.kind === "election";

  const scene = (
    <div
      className={"ed-layer" + (props.wasHitler && flipped ? " ed-hitler" : "") + (flipped ? " ed-revealed" : "")}
      role="dialog"
      aria-modal="true"
      aria-label={election ? "Chancellor elected" : "Execution"}
    >
      <div className="ed-vignette" />
      <div className="ed-stage">
        <div className="ed-kicker">{election ? "CHANCELLOR ELECTED" : "EXECUTION"}</div>
        <div className="ed-who">
          {portrait ? <img className="ed-avatar" src={portrait} alt="" /> : <span className="ed-avatar ed-avatar-empty">{props.target.charAt(0)}</span>}
          <div className="ed-text">
            <div className="ed-name">{props.target}</div>
            <div className="ed-by">
              {election ? "was elected Chancellor" : "was chosen by " + props.president}
            </div>
          </div>
        </div>

        <div className="ed-scene" aria-live="polite">
          <div className={"ed-countdown" + (phase === "count" ? "" : " ed-countdown-gone")}>
            {phase === "count" && (
              <div key={count} className={"ed-number ed-number-" + count} aria-label={String(count)}>
                {count}
              </div>
            )}
          </div>

          <div className={"ed-card" + (phase === "count" ? " ed-card-count" : "") + (flipped ? " ed-card-flipped" : "") + (phase === "beat" ? " ed-card-hold" : "") + (reduced ? " ed-card-still" : "")}>
            <div className="ed-card-inner">
              <div className="ed-face ed-back" aria-hidden="true">
                <div className="ed-back-q">?</div>
              </div>
              <div className={"ed-face ed-front " + (props.wasHitler ? "ed-front-hitler" : "ed-front-safe")}>
                <div className="ed-front-title">{props.wasHitler ? "HITLER" : "NOT HITLER"}</div>
                <div className="ed-front-sub">
                  {props.wasHitler ? (election ? "The Fascists win." : "The Liberals win.") : "The game goes on."}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="ed-footer">
          {phase === "done" ? (
            <>
              <p className="ed-result">
                {election
                  ? props.wasHitler
                    ? props.target + " is Hitler, and the table has elected them Chancellor."
                    : props.target + " is not Hitler."
                  : props.target + " has been executed and may no longer speak, vote or run for office."}
              </p>
              <button ref={okay} className="ed-okay" onClick={finish}>
                {props.wasHitler ? "CONTINUE" : "OKAY"}
              </button>
            </>
          ) : (
            <button className="ed-skip" onClick={skip}>
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // In a portal so that the alert box's own positioning cannot shrink the scene.
  return ReactDOM.createPortal(scene, document.body) as unknown as ReactElement;
}
